'use client'

import { compact } from 'lodash'
import { useMemo, useState, useEffect, useRef } from 'react'
import { Spacer, Badge, Text, Icon, Divider } from '@vinted/web-ui'
import { ArrowLeft24, ChevronRight24 } from '@vinted/monochrome-icons'
import { v4 as uuid } from 'uuid'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  flattenDynamicFilterOptions,
  transformDynamicFilterNavigationalOptions,
  DynamicFilterOptionModel,
  FilterCatalogModel,
  FilterGroupModel,
  OptionType,
} from '@marketplace-web/catalog/catalog-data'
import { useCatalogContext, clickEvent } from '@marketplace-web/catalog/catalog-page-data'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/catalog/catalog-filters-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import FilterCell from '../../../FilterCell'
import FilterModal from '../../../FilterModal'
import FilterModalNavigation from '../../../FilterModalNavigation'
import FilterCatalogList from '../../../FilterCatalogList/FilterCatalogList'
import FilterCellSuffix from '../../../FilterCell/FilterCellSuffix'
import GridGroup from '../Common/GridGroup'
import VerticalPadder from '../Common/VerticalPadder'

type Props = {
  type: string
  title: string
  isSelectionHighlighted?: boolean
  isNewFilter?: boolean
  options: Array<DynamicFilterOptionModel>
  selectedIds: Array<number>
  isFilterCellVisible: boolean
  fromScreen: string
  rootOptionType: OptionType
  isHidden?: boolean
  totalItemsCount?: number | null
  onListOptionSelect: (id: number) => void
  clearSelectedOptions: (type: string, ids?: Array<number>) => void
  onSubmit: () => void
  onFilterCellClick: () => void
  onFilterCellView: () => void
  onModalOpen: () => void
  onModalClose: () => void
}

const GridFilterModal = ({
  type,
  title,
  isSelectionHighlighted,
  isNewFilter,
  options,
  selectedIds,
  isFilterCellVisible,
  rootOptionType,
  fromScreen,
  isHidden,
  totalItemsCount,
  onListOptionSelect,
  clearSelectedOptions,
  onSubmit,
  onFilterCellClick,
  onFilterCellView,
  onModalOpen,
  onModalClose,
}: Props) => {
  const translate = useTranslate()
  const { track } = useTracking()
  const {
    state: {
      items: { searchTrackingParams },
    },
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchTrackingParams
  const searchSessionId = searchTrackingParams.sessionId
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCatalogCode, setSelectedCatalogCode] = useState<string>('')
  const [highlightedSelectedIds, setHighlightedSelectedIds] = useState<Array<number>>(selectedIds)

  const seenItems = useRef(new Set<number>())
  const filteringSessionId = useRef('')

  const screen = `${'dynamic_filter'}_${type}`

  useEffect(() => {
    if (isModalOpen) {
      filteringSessionId.current = uuid()

      return
    }

    seenItems.current.clear()
    filteringSessionId.current = ''
  }, [isModalOpen])

  useEffect(() => {
    if (rootOptionType === OptionType.Navigational) {
      setSelectedCatalogCode('')

      return
    }

    setSelectedCatalogCode(OptionType.Root)
  }, [rootOptionType])

  const flattedOptions = useMemo(() => {
    return flattenDynamicFilterOptions(options)
  }, [options])

  const navigationalItems = useMemo(() => {
    return transformDynamicFilterNavigationalOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const rootSuffixText = useMemo(() => {
    return compact(
      selectedIds.map(selectedId => flattedOptions.find(option => option.id === selectedId)?.title),
    ).join(', ')
  }, [selectedIds, flattedOptions])

  const navigatedRoot = useMemo(() => {
    return options.find(item => selectedCatalogCode === `${item.type}_${item.id}`)
  }, [options, selectedCatalogCode])

  const navigatedGroups = useMemo(() => {
    if (selectedCatalogCode === OptionType.Root) return options

    return navigatedRoot?.options || []
  }, [navigatedRoot?.options, options, selectedCatalogCode])

  const navigatedGroupsSelected = useMemo(() => {
    return navigatedGroups
      ?.map(item => ({
        ...item,
        options: item.options.filter(option => highlightedSelectedIds.includes(option.id)),
      }))
      .filter(item => item.options.length > 0)
  }, [highlightedSelectedIds, navigatedGroups])

  const groupToCount = useMemo(() => {
    const map = new Map<number, number>()

    navigatedGroups?.forEach(group => {
      let count = 0

      group.options.forEach(item => {
        if (selectedIds.includes(item.id)) {
          count += 1
        }
      })

      map.set(group.id, count)
    })

    return map
  }, [navigatedGroups, selectedIds])

  const getItemPosition = (id: number) => {
    return flattedOptions.findIndex(item => item.id === id) + 1
  }

  const openModal = () => {
    setIsModalOpen(true)
    setHighlightedSelectedIds(selectedIds)
    onModalOpen()
  }

  const closeModal = () => {
    setIsModalOpen(false)
    onModalClose()
  }

  const handleFilterCellClick = () => {
    openModal()
    onFilterCellClick()
  }

  const handleNavClick = (catalog: FilterCatalogModel) => {
    setSelectedCatalogCode(catalog.code)
  }

  const handleNavBackClick = () => {
    setSelectedCatalogCode('')
    setHighlightedSelectedIds(selectedIds)
  }

  const handleSubmitClick = () => {
    closeModal()
    onSubmit()

    track(
      clickEvent({
        screen,
        target: 'apply_filters',
      }),
    )
  }

  const handleSelect = (item: DynamicFilterOptionModel) => {
    onListOptionSelect(item.id)

    track(
      userClickDynamicFilterOptionEvent({
        id: item.id,
        type,
        code: type,
        position: getItemPosition(item.id),
        itemsCount: item.itemsCount,
        isSelected: !selectedIds.includes(item.id),
        filteringSessionId: filteringSessionId.current,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )
  }

  const handleInViewItem = (item: DynamicFilterOptionModel) => {
    if (seenItems.current.has(item.id)) return
    seenItems.current.add(item.id)

    track(
      userViewDynamicFilterOptionEvent({
        id: item.id,
        type,
        code: type,
        position: getItemPosition(item.id),
        itemsCount: item.itemsCount,
        filteringSessionId: filteringSessionId.current,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )
  }

  const clearSelectedOptionIds = () => {
    track(
      clickEvent({
        target: 'clear_filters',
        screen,
      }),
    )

    if (!navigatedRoot) {
      clearSelectedOptions(
        type,
        flattedOptions.map(item => item.id),
      )

      return
    }

    clearSelectedOptions(
      type,
      navigatedGroups.flatMap(group => group.options).map(item => item.id),
    )
  }

  const getSelectedSuffix = (groups: Array<FilterGroupModel<DynamicFilterOptionModel>>) => {
    const items = groups.flatMap(group => group.items)

    return compact(
      selectedIds.map(selectedId => items.find(({ id }) => selectedId === id)?.title),
    ).join(', ')
  }

  const renderTitle = () => (
    <>
      <Text text={title} type="title" as="span" />
      {isNewFilter && (
        <>
          <Spacer orientation="vertical" />
          <Badge theme="primary" content={translate('common.new_badge')} />
        </>
      )}
    </>
  )

  const renderModalNavigation = () => {
    const modalTitle = navigatedRoot?.title || title
    const modalPrefixClick = navigatedRoot ? handleNavBackClick : closeModal

    return (
      <FilterModalNavigation
        title={modalTitle}
        icon={
          <Icon
            name={ArrowLeft24}
            color="greyscale-level-1"
            testId={`${type}-navigation-prefix-icon`}
          />
        }
        prefixAriaLabel={translate('catalog.filters.a11y.actions.back')}
        suffixText={translate('catalog.filters.dynamic.clear')}
        onPrefixClick={modalPrefixClick}
        onSuffixClick={clearSelectedOptionIds}
      />
    )
  }

  const renderCatalogCellSuffix = (groups: Array<FilterGroupModel<DynamicFilterOptionModel>>) => (
    <FilterCellSuffix
      suffixText={getSelectedSuffix(groups)}
      textTheme="primary"
      renderSuffix={() => <Icon name={ChevronRight24} color="greyscale-level-3" />}
    />
  )

  const renderContentHeader = () => {
    if (!selectedCatalogCode) return undefined
    if ((navigatedGroupsSelected?.length || 0) < 1) return undefined
    if (!isSelectionHighlighted) return undefined

    return (
      <>
        <Spacer size="large" />
        <VerticalPadder>
          <Text
            text={translate('catalog.filters.dynamic.applied_filters')}
            type="subtitle"
            as="span"
          />
        </VerticalPadder>
        {navigatedGroupsSelected?.map(gridItem => (
          <GridGroup
            key={gridItem.id}
            option={gridItem}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onInViewItem={handleInViewItem}
          />
        ))}
        <Spacer size="large" />
        <Divider />
      </>
    )
  }

  const renderContent = () => {
    if (!selectedCatalogCode) {
      return (
        <FilterCatalogList
          renderSuffix={renderCatalogCellSuffix}
          catalogs={navigationalItems}
          onItemClick={handleNavClick}
        />
      )
    }

    return navigatedGroups?.map(gridItem => (
      <GridGroup
        key={gridItem.id}
        isSticky
        selectedCount={groupToCount.get(gridItem.id)}
        option={gridItem}
        selectedIds={selectedIds}
        onSelect={handleSelect}
        onInViewItem={handleInViewItem}
      />
    ))
  }

  if (isHidden) return null

  return (
    <>
      <FilterCell
        title={renderTitle()}
        suffixText={rootSuffixText || translate('catalog.filters.dynamic.all')}
        suffixTextTheme={rootSuffixText ? 'primary' : undefined}
        onClick={handleFilterCellClick}
        onView={onFilterCellView}
        visible={isFilterCellVisible}
      />

      <FilterModal
        renderNavigation={renderModalNavigation}
        isOpen={isModalOpen}
        totalItemsCount={totalItemsCount}
        onSubmit={handleSubmitClick}
        testId={`${type}-filter-modal`}
      >
        {renderContentHeader()}
        {renderContent()}
      </FilterModal>
    </>
  )
}

export default GridFilterModal
